/**
 * Moisture test arithmetic: seedtrac's QC handheld Moisture form (deck `Moisture.pptx`, legacy
 * `MoistureReadingFormActivity`) and the QCS Moisture % verification (manual p170–171).
 *
 * Pure functions shared by the API, which stores the figures, and both clients, which show them
 * live while the analyst types, so a percentage can never be worked out two different ways.
 *
 *   Moisture Meter   three replications (the first two required), the Result is their mean
 *   Hot Air Oven     four replications of three weighings (the first two required):
 *                      M1 empty container with lid
 *                      M2 container, lid and sample before drying
 *                      M3 the same after drying
 *                    each replication's % is (M2 − M3) / (M2 − M1) × 100, and the Result is their mean
 */
import { z } from "zod";

/** How the analyst measured. */
export const MOISTURE_METHODS = ["meter", "oven"] as const;
export type MoistureMethod = (typeof MOISTURE_METHODS)[number];
export const moistureMethod = z.enum(MOISTURE_METHODS);

export const MOISTURE_METHOD_LABELS: Record<MoistureMethod, string> = {
  meter: "Moisture Meter",
  oven: "Hot Air Oven Method",
};

/** Which reading a verification settled on: one of the analyst's methods, or the supervisor's own. */
export const MOISTURE_SOURCES = ["meter", "oven", "supervisor"] as const;
export type MoistureSource = (typeof MOISTURE_SOURCES)[number];

/** The Moisture Test Result, against the crop's maximum moisture. */
export const MOISTURE_RESULTS = ["pass", "fail"] as const;
export type MoistureResult = (typeof MOISTURE_RESULTS)[number];
export const moistureResultSchema = z.enum(MOISTURE_RESULTS);

/**
 * Moisture never fails a lot (user, 2026-10-31): a reading over the crop's maximum is High moisture
 * and the lot stays acceptable. The stored value stays `fail`; no screen ever says Fail for it.
 */
export const MOISTURE_RESULT_LABELS: Record<MoistureResult, string> = {
  pass: "Pass",
  fail: "High moisture",
};

/** A verified moisture over the crop's maximum: shown as High moisture, never as a failure. */
export const isHighMoisture = (v: string | null | undefined): boolean =>
  v === "fail";

/** Where a sample's moisture test has got to. */
export const MOISTURE_STATUSES = [
  "not_applicable",
  "not_started",
  "in_progress",
  "submitted",
  "verified",
] as const;
export type MoistureStatus = (typeof MOISTURE_STATUSES)[number];

export const MOISTURE_METER_REPS = 3;
export const MOISTURE_OVEN_REPS = 4;
/**
 * Once any oven weighing is entered, replications 1–2 need all of M1, M2 and M3; the 3rd and 4th are
 * optional (user, 2026-10-23: "only 2 readings would be mandatory"; was 1–3 from 2026-10-17).
 */
export const MOISTURE_OVEN_REQUIRED_REPS = 2;

/**
 * The band a meter reading must fall in. The legacy screen refused anything outside it; nexseed
 * checks every replication entered, and the mean, rather than only the first replication.
 */
export const MOISTURE_METER_RANGE = { min: 4.5, max: 20 } as const;

export function outOfMeterRange(value: number): boolean {
  return value < MOISTURE_METER_RANGE.min || value > MOISTURE_METER_RANGE.max;
}

const roundTo = (value: number, dp: number): number => {
  const f = 10 ** dp;
  return Math.round((value + Number.EPSILON) * f) / f;
};

/**
 * A moisture % as the lab reads and judges it: one decimal (user, 2026-10-31). The verdict compares
 * this figure, so what the screen shows is what passes or fails.
 */
export const roundPct = (value: number): number => roundTo(value, 1);

/** A moisture % for display, one decimal; a dash when there is no figure. */
export function formatPct(value: number | string | null | undefined): string {
  if (value === null || value === undefined || value === "") return "—";
  const n = typeof value === "number" ? value : Number(value);
  return Number.isFinite(n) ? roundPct(n).toFixed(1) : "—";
}

const taken = (values: readonly (number | null | undefined)[]): number[] =>
  values.filter(
    (v): v is number => typeof v === "number" && Number.isFinite(v)
  );

/** The meter's Result: the mean of the replications actually taken (blanks skipped), one decimal. */
export function meterMean(
  reps: readonly (number | null | undefined)[]
): number | null {
  const got = taken(reps);
  if (!got.length) return null;
  return roundTo(got.reduce((a, b) => a + b, 0) / got.length, 1);
}

/**
 * One oven replication, one decimal. Null until all three weights are there, the sample actually
 * weighs something (M2 > M1), and it did not gain weight in the oven (M1 ≤ M3 ≤ M2).
 */
export function ovenRepPct(
  m1: number | null | undefined,
  m2: number | null | undefined,
  m3: number | null | undefined
): number | null {
  if (m1 == null || m2 == null || m3 == null) return null;
  if (!(m2 > m1) || m3 > m2 || m3 < m1) return null;
  return roundTo(((m2 - m3) / (m2 - m1)) * 100, 1);
}

/** The oven's Result: the mean of the replication percentages that exist, two decimals. */
export function ovenMean(
  pcts: readonly (number | null | undefined)[]
): number | null {
  const got = taken(pcts);
  if (!got.length) return null;
  return roundTo(got.reduce((a, b) => a + b, 0) / got.length, 2);
}

/** Every oven replication's % for the four weighings, in order. */
export function ovenRepPcts(
  m1: readonly (number | null | undefined)[],
  m2: readonly (number | null | undefined)[],
  m3: readonly (number | null | undefined)[]
): (number | null)[] {
  return Array.from({ length: MOISTURE_OVEN_REPS }, (_, i) =>
    ovenRepPct(m1[i], m2[i], m3[i])
  );
}

/**
 * Pass when the moisture, rounded to one decimal, is at or under the crop's maximum, Fail over it, and null when the crop has
 * no maximum set, in which case the supervisor decides.
 */
export function moistureResult(
  pct: number,
  maxPct: number | null | undefined
): MoistureResult | null {
  if (maxPct == null) return null;
  return roundPct(pct) <= maxPct ? "pass" : "fail";
}

/**
 * What a verification settles on. The supervisor's own reading, when taken, wins over whichever of
 * the analyst's methods is picked (the user's call, 2026-09-13). Null when the picked method has no
 * figure and there is no supervisor reading.
 */
export function verifiedMoisture(
  test: {
    meterPct: number | null;
    ovenPct: number | null;
    supervisorPct: number | null;
  },
  method: MoistureMethod
): { source: MoistureSource; pct: number } | null {
  if (test.supervisorPct != null)
    return { source: "supervisor", pct: roundPct(test.supervisorPct) };
  const pct = method === "meter" ? test.meterPct : test.ovenPct;
  return pct == null ? null : { source: method, pct: roundPct(pct) };
}

/** A replication value off a form: a number, or null for a box left blank. */
const repValue = z.number().min(0).max(100).nullable();
/** A weighing in grams, three decimals on the lab balance. */
const weight = z.number().positive().max(99_999).nullable();

/** Meter replications 1–3; the first two are required, as on the legacy form. */
export const moistureMeterReps = z
  .array(repValue)
  .length(MOISTURE_METER_REPS)
  .refine((r) => r[0] != null && r[1] != null, {
    message: "Replications 1 and 2 are required",
  });

/** One oven weighing stage: four replications. */
export const moistureOvenWeights = z
  .array(weight)
  .length(MOISTURE_OVEN_REPS)
  .refine((w) => w.some((x) => x != null), {
    message: "Enter at least one replication",
  });
